/**
 * AudioReactiveEngine — one audio source in, per-frame "drivers" for visuals out.
 *
 *   var eng = AudioReactiveEngine.attach(ctx, source, {
 *       slug: 'audio-visualizer',
 *       route: 'through',          // 'through': the audio also goes to the speakers (a played file)
 *                                   // 'tap':     analyse only (a microphone — never to the speakers)
 *       coreUrls:    [...],        // versioned URLs of core.js / worklet.js for addModule
 *       workletUrls: [...]
 *   });
 *   await eng.ready;               // worklet loaded, or the AnalyserNode fallback armed
 *   // every animation frame:
 *   var d = eng.sample();          // AudioReactiveCore Tracker drivers (live object — read, do not keep)
 *   var hits = eng.takeEvents();   // [{t, g, s}] since the last call: g 0 kick, 1 snare, 2 hat, 3 beat
 *   eng.clock();                   // the graph time `d` describes (the hits' t is on this clock)
 *   eng.setSensitivity(1.4);
 *   eng.setAlignment('graph');     // while recording: the recorded audio carries no output latency
 *   eng.reset();                   // a new song: forget levels and tempo
 *   eng.detach();
 *
 * What "now" means (docs/why/primitives/audio-reactive.md):
 *   route 'through' — the moment the person HEARS (AudibleClock), so a pulse
 *     lands with the hit, not outputLatency early (Bluetooth: 150-300 ms);
 *   route 'tap'     — the newest frame: the sound was heard before it reached
 *     the microphone, there is nothing to wait for.
 * A frame is stamped with the time of its last sample, while its content is
 * centred CONTENT_LEAD_S earlier (median onset lag on the corpus: 15 ms).
 *
 * Paths: AudioWorklet (Chrome 66+, Firefox 76+, Safari 14.1+) — every hop
 * analysed once on the audio thread; otherwise an AnalyserNode read on each
 * sample() — same core, frame-rate hops. eng.kind tells which.
 *
 * ES5. Needs AudioReactiveCore (core.js) on the page; AudibleClock and
 * AudioWorkletLoader when present.
 */
(function (root) {
    'use strict';

    var CONTENT_LEAD_S = 0.015;
    var MAX_QUEUE_S = 1.5;        /* a hidden tab: frames arrive, nobody samples — keep up anyway */

    function now() { return (root.performance && performance.now) ? performance.now() : Date.now(); }

    function Engine(ctx, source, opts) {
        var Core = root.AudioReactiveCore;
        if (!Core) throw new Error('AudioReactiveEngine: core.js is not loaded');
        this.Core = Core;
        this.ctx = ctx;
        this.source = source;
        this.opts = opts || {};
        this.route = this.opts.route === 'through' ? 'through' : 'tap';
        this.alignment = 'audible';
        this.tracker = new Core.Tracker(ctx.sampleRate, { background: this.route === 'through' ? 'silence' : 'learn' });
        this.queue = [];
        this.events = [];
        this.kind = 'pending';
        this.nodes = [];
        this.detached = false;
        this.stats = { frames: 0, samples: 0, maxLagMs: 0, started: now(), lockedFrames: 0, presentFrames: 0, onsets: 0 };
        var self = this;
        this.ready = this._startWorklet().then(function (ok) {
            if (!ok && !self.detached) self._startAnalyser();
            return self.kind;
        });
    }

    Engine.prototype._connectOut = function (node) {
        var ctx = this.ctx;
        if (this.route === 'through') {
            node.connect(ctx.destination);
        } else {
            var mute = ctx.createGain();
            mute.gain.value = 0;
            node.connect(mute);
            mute.connect(ctx.destination);
            this.nodes.push(mute);
        }
    };

    Engine.prototype._startWorklet = function () {
        var self = this, ctx = this.ctx, o = this.opts;
        var Loader = root.AudioWorkletLoader;
        if (!ctx.audioWorklet || typeof root.AudioWorkletNode !== 'function' || !Loader || !o.coreUrls || !o.workletUrls) {
            return Promise.resolve(false);
        }
        /* one pair of addModule calls per context, however many times a source is attached */
        if (!ctx.__arModules) {
            ctx.__arModules = Loader.load(ctx, o.coreUrls, { slug: o.slug })
                .then(function () { return Loader.load(ctx, o.workletUrls, { slug: o.slug }); });
            ctx.__arModules.catch(function () { ctx.__arModules = null; });
        }
        return ctx.__arModules.then(function () {
            if (self.detached) return true;
            var an = new self.Core.Analyzer(ctx.sampleRate);
            var node = new root.AudioWorkletNode(ctx, 'audio-reactive-analyzer', {
                numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
                processorOptions: { fftSize: an.fftSize, hop: an.hop, batch: 2 }
            });
            node.port.onmessage = function (e) { self._onFrames(e.data); };
            self.source.connect(node);
            self._connectOut(node);
            self.node = node;
            self.nodes.push(node);
            self.kind = 'worklet';
            return true;
        }, function () {
            return false;
        });
    };

    Engine.prototype._startAnalyser = function () {
        var ctx = this.ctx;
        var an = new this.Core.Analyzer(ctx.sampleRate);
        var a = ctx.createAnalyser();
        a.fftSize = an.fftSize;
        a.smoothingTimeConstant = 0;
        this.source.connect(a);
        if (this.route === 'through') this.source.connect(ctx.destination);
        this.fallbackAnalyzer = an;
        this.fallbackNode = a;
        this.fallbackBuf = new Float32Array(an.fftSize);
        this.fallbackFrame = new Float64Array(this.Core.FRAME_LEN);
        this.nodes.push(a);
        this.kind = 'analyser';
    };

    Engine.prototype._onFrames = function (buf) {
        if (this.detached || !buf || !buf.length) return;
        var L = this.Core.FRAME_LEN;
        for (var off = 0; off + L <= buf.length; off += L) this.queue.push(buf.subarray(off, off + L));
        /* nobody is sampling (hidden tab): step the oldest frames now so the
           queue never grows and the state is current when the tab returns */
        var q = this.queue;
        while (q.length && q[q.length - 1][0] - q[0][0] > MAX_QUEUE_S) this._step(q.shift());
    };

    Engine.prototype._step = function (frame) {
        var tr = this.tracker;
        tr.step(frame);
        var ev = this.events;
        if (ev.length < 64) {
            for (var g = 0; g < 3; g++) if (tr.onsetEvent[g] > 0) ev.push({ t: tr.onsetEventT[g], g: g, s: tr.onsetEvent[g] });
            if (tr.beatEvent) ev.push({ t: frame[0], g: 3, s: 1 });
        }
        var st = this.stats;
        if (!st.frames) st.t0 = frame[0];
        st.t1 = frame[0];
        st.frames++;
        if (this.tracker.locked) st.lockedFrames++;
        if (this.tracker.presence > 0.5) st.presentFrames++;
        if (this.tracker.onsetEvent[0] || this.tracker.onsetEvent[1] || this.tracker.onsetEvent[2]) st.onsets++;
    };

    /* Graph time whose sound is being heard now (plus the analysis lead). */
    Engine.prototype._target = function () {
        var ctx = this.ctx, t = ctx.currentTime;
        if (this.route === 'through' && this.alignment === 'audible') {
            var AC = root.AudibleClock;
            t = AC ? AC.audible(ctx, t) : t;
        }
        return t + CONTENT_LEAD_S;
    };

    Engine.prototype.sample = function () {
        if (this.detached) return this.tracker.d;
        this.stats.samples++;
        if (this.kind === 'analyser') {
            this.fallbackNode.getFloatTimeDomainData(this.fallbackBuf);
            this.fallbackAnalyzer.analyze(this.fallbackBuf);
            this.fallbackAnalyzer.writeFrame(this.ctx.currentTime, this.fallbackFrame);
            this.queue.push(Float64Array.from(this.fallbackFrame));
        }
        var q = this.queue;
        if (!q.length) return this.tracker.d;
        if (this.route === 'tap') {
            while (q.length) this._step(q.shift());
        } else {
            var target = this._target();
            while (q.length && q[0][0] <= target) this._step(q.shift());
            var lag = (q.length ? q[q.length - 1][0] : target) - target;
            if (lag * 1000 > this.stats.maxLagMs) this.stats.maxLagMs = lag * 1000;
        }
        return this.tracker.d;
    };

    Engine.prototype.takeEvents = function () {
        var e = this.events;
        this.events = [];
        return e;
    };
    Engine.prototype.clock = function () { return this.tracker.t > 0 ? this.tracker.t : this.ctx.currentTime; };

    Engine.prototype.setSensitivity = function (s) { this.tracker.setSensitivity(s); };
    Engine.prototype.setAlignment = function (a) { this.alignment = a === 'graph' ? 'graph' : 'audible'; };

    Engine.prototype.reset = function () {
        this.queue.length = 0;
        this.events.length = 0;
        this.tracker.reset();
    };

    /* Summary for telemetry: which path ran and how the music was heard. */
    Engine.prototype.summary = function () {
        var st = this.stats, f = Math.max(1, st.frames);
        var AC = root.AudibleClock;
        return {
            kind: this.kind,
            route: this.route,
            sr: this.ctx.sampleRate,
            sec: Math.round((now() - st.started) / 1000),
            locked: Math.round(100 * st.lockedFrames / f),
            present: Math.round(100 * st.presentFrames / f),
            onsetsPerMin: st.frames > 1 ? Math.round(st.onsets * 60 / Math.max(1, st.t1 - st.t0)) : 0,
            lagMs: AC ? Math.round(AC.lagS(this.ctx) * 1000) : null
        };
    };

    Engine.prototype.detach = function () {
        if (this.detached) return;
        this.detached = true;
        try { if (this.node) { this.node.port.onmessage = null; this.node.port.postMessage('stop'); } } catch (e) {}
        try { this.source.disconnect(this.fallbackNode || this.node); } catch (e) {}
        if (this.route === 'through' && this.kind === 'analyser') { try { this.source.disconnect(this.ctx.destination); } catch (e) {} }
        for (var i = 0; i < this.nodes.length; i++) { try { this.nodes[i].disconnect(); } catch (e) {} }
        this.nodes.length = 0;
        this.queue.length = 0;
    };

    root.AudioReactiveEngine = {
        attach: function (ctx, source, opts) { return new Engine(ctx, source, opts); },
        CONTENT_LEAD_S: CONTENT_LEAD_S
    };
})(typeof window !== 'undefined' ? window : this);
